import { useState, useEffect } from "react";
import { Activity } from "../types";
import { useToast, Button } from "@repo/o37-ui";

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

export default function ActivityTimeline({ dealId, partnerId }: { dealId: string; partnerId: string }) {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [note, setNote] = useState("");
  const [activityType, setActivityType] = useState<Activity['type']>('note');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    loadActivities();
  }, [dealId]);

  const loadActivities = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/activities/deal/${dealId}`);
      if (response.ok) {
        const data = await response.json();
        setActivities(data);
      }
    } catch (error) {
      console.error("Failed to load activities:", error);
    } finally {
      setLoading(false);
    }
  };

  const addNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!note.trim()) return;

    try {
      setSubmitting(true);
      const response = await fetch(`${API_URL}/activities`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dealId,
          partnerId,
          type: activityType,
          content: note,
        }),
      });

      if (response.ok) {
        setNote("");
        setActivityType("note");
        loadActivities();
      } else {
        showToast("Failed to add note", "error");
      }
    } catch (error) {
      console.error("Failed to add note:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "numeric" });
  };

  const getIcon = (type: string) => {
    switch (type) {
      case "note": return "📝";
      case "call": return "📞";
      case "email": return "📧";
      case "meeting": return "📅";
      case "stage_change": return "🔄";
      case "creation": return "✨";
      default: return "•";
    }
  };

  if (loading) return <div className="text-xs text-neutral-500 py-4 text-center">Loading timeline...</div>;

  return (
    <div className="h-full flex flex-col">
      {/* Activity List */}
      <div className="flex-1 overflow-y-auto min-h-[200px] max-h-[300px] pr-2 space-y-4 mb-4">
        {activities.length === 0 ? (
          <div className="text-xs text-neutral-400 text-center italic py-4">No activity yet.</div>
        ) : (
          activities.map((activity) => (
            <div key={activity.id} className="flex gap-3 text-sm">
              <div className="mt-0.5 text-neutral-400 select-none text-lg" title={activity.type}>
                {getIcon(activity.type)}
              </div>
              <div className="flex-1">
                <div className="bg-neutral-50 rounded-lg p-3 border border-neutral-100">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium uppercase tracking-wide text-neutral-500">{activity.type.replace('_', ' ')}</span>
                  </div>
                  <p className="text-neutral-800 whitespace-pre-wrap">{activity.content}</p>
                </div>
                <div className="mt-1 text-[10px] text-neutral-400 pl-1">{formatDate(activity.createdAt)}</div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Activity Input */}
      <form onSubmit={addNote} className="relative bg-neutral-50 p-3 rounded-xl border border-neutral-200">
        <div className="flex gap-2 mb-2">
          {(['note', 'call', 'email', 'meeting'] as const).map((type) => (
            <Button
              key={type}
              type="button"
              variant={activityType === type ? "primary" : "outline"}
              size="sm"
              onClick={() => setActivityType(type)}
              className="text-xs h-7"
              text={
                type === 'note' ? "📝 Note" :
                type === 'call' ? "📞 Call" :
                type === 'email' ? "📧 Email" :
                "📅 Meeting"
              }
            />
          ))}
        </div>
        
        <div className="relative">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={`Add a ${activityType} description...`}
            className="w-full text-sm bg-white border border-neutral-200 rounded-xl p-3 pr-12 focus:outline-none focus:ring-2 focus:ring-neutral-800 resize-none h-[80px]"
          />
          <Button
            type="submit"
            variant="primary"
            disabled={!note.trim() || submitting}
            className="absolute bottom-3 right-3 p-1.5 h-auto w-auto min-h-0 rounded-lg hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Save Activity"
            icon={
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            }
          />
        </div>
      </form>
    </div>
  );
}

